import {
  TAX_ID_AUTOLIQUIDATION_ETRANGER,
  TAX_ID_FRANCHISE_293B,
  TAX_ID_TVA_ETRANGERE,
  TAX_ID_TVA_NON_DEDUCTIBLE,
  tvaNonDeductible,
  type ProductType,
  type TaxBasisType,
} from './zoho-france';

/**
 * Le régime de TVA d'une facture fournisseur (spec §2).
 *
 * Fonction PURE : elle décide, elle ne lit rien. C'est ce qui permet de la
 * tester sans base ni réseau, et d'être sûr qu'un même justificatif produira
 * toujours le même routage.
 *
 * Trois cas automatisables. Tout le reste part en validation manuelle AVEC SON
 * MOTIF : une facture fausse coûte plus cher qu'une facture non créée.
 *
 * Le routage se fonde sur la TVA EFFECTIVEMENT PORTÉE PAR LA FACTURE, jamais
 * sur un statut mémorisé quelque part. Un auto-entrepreneur qui franchit le
 * seuil devient assujetti du jour au lendemain : c'est sa facture qui le dit,
 * pas sa fiche.
 */

export type RegimeTva =
  /** Facture française avec TVA : la taxe du taux, ligne par ligne. */
  | { cas: 'A'; taxBasisType: TaxBasisType }
  /** Fournisseur étranger sans TVA : taxe à 0 % « autoliquidation ». */
  | { cas: 'B'; taxId: typeof TAX_ID_AUTOLIQUIDATION_ETRANGER; taxBasisType: TaxBasisType }
  /** Fournisseur français sans TVA : taxe à 0 % « franchise en base 293 B ». */
  | { cas: 'C'; taxId: typeof TAX_ID_FRANCHISE_293B; taxBasisType: TaxBasisType }
  /** TVA d'un autre pays, payée sur place et non récupérable en France. */
  | { cas: 'E'; taxId: typeof TAX_ID_TVA_ETRANGERE; taxBasisType: TaxBasisType }
  /**
   * TVA FRANÇAISE facturée par un fournisseur étranger qui n'est pas immatriculé
   * en France (Zoho Corporation B.V. et son numéro NL, 28/10/2026). Jamais
   * déduit : c'est une DÉCISION humaine prise à l'écran, la charge part en TTC.
   */
  | { cas: 'NR'; taxId: typeof TAX_ID_TVA_NON_DEDUCTIBLE; taxBasisType: TaxBasisType }
  | { cas: 'manuel'; motif: string };

/** Ce que l'extraction sait du pays du fournisseur. */
export type PaysFournisseur = 'FR' | 'UE' | 'HORS_UE' | null | undefined;

export interface EntreesRegime {
  pays: PaysFournisseur;
  /** Au moins une ligne de TVA sur le justificatif. */
  tvaPresente: boolean | null | undefined;
  /** Mention « TVA acquittée d'après les débits » ou équivalent. */
  mentionTvaSurLesDebits?: boolean | null;
  /** Nature des lignes, pour départager `accrual` et `cash`. */
  typesProduit?: ProductType[];
  /**
   * Type de dépense détecté à l'extraction. Décide du cas « TVA non
   * déductible » : sur un transport de personnes, la question « y a-t-il de la
   * TVA ? » ne se pose pas, puisque la réponse ne change pas le traitement.
   *
   * OBLIGATOIRE, quitte à valoir `null`. Il a d'abord été optionnel, et le flux
   * factures fournisseurs a passé ce type à `tvaPresenteEffective` en oubliant
   * de le passer ICI : la règle de non-déduction ne s'appliquait jamais, et
   * tous les justificatifs de train partaient en saisie manuelle. Le rendre
   * requis fait dire cet oubli par le compilateur.
   */
  typeDepense: string | null | undefined;
  /**
   * Numéro de TVA intracommunautaire du fournisseur, tel qu'il figure sur la
   * facture. Un numéro commençant par « FR » prouve une immatriculation
   * française : ce fournisseur-là facture la TVA française, quel que soit le
   * pays de son siège.
   */
  numeroTvaIntra: string | null | undefined;
  /**
   * Les taux portés par les lignes validées. Conservés dans les entrées parce
   * qu'ils décrivent la facture, même si le rattachement à la France se prouve
   * par le seul numéro de TVA : un taux se pratique dans plusieurs pays.
   */
  tauxDesLignes: Array<number | null | undefined>;
  /**
   * Le justificatif porte-t-il la TVA d'un AUTRE PAYS ? Constat de l'extraction.
   * Il tranche avant tout le reste : ni le pays du fournisseur ni la présence de
   * TVA ne suffisent à distinguer ce cas d'une autoliquidation, qui est son
   * contraire exact.
   */
  tvaEtrangere?: boolean | null;
  /**
   * La personne qui valide a déclaré cette TVA non récupérable (case de
   * l'écran). Elle ne fait que REMPLACER le refus « TVA non rattachable à la
   * France » : un numéro FR, une TVA étrangère ou l'absence de TVA gardent leur
   * propre cas, parce qu'ils sont tranchés avant.
   */
  tvaNonRecuperableDecidee?: boolean;
}

/** Types de dépense dont les règles d'extraction VIDENT la TVA (non récupérable). */
const TVA_ECARTEE_PAR_LES_REGLES = ['transport', 'hebergement', 'postal'];

/**
 * La TVA est-elle EFFECTIVEMENT portée par la facture ?
 *
 * Trois sources, dans cet ordre, et l'ordre est le fond du sujet :
 *
 * 1. **Les lignes validées.** Si une ligne porte un taux, la TVA est là. C'est
 *    la preuve la plus forte : ces lignes ont été relues par un humain, le
 *    justificatif sous les yeux. Elles priment sur tout constat automatique.
 * 2. **Le constat de l'extraction** (`tva_presente`), quand il existe. Il est
 *    absent de toutes les factures extraites avant le 11/09/2026 — dont la
 *    file d'attente du jour, ce qui les bloquait toutes.
 * 3. **Le type de dépense**, en dernier recours. Les règles d'extraction VIDENT
 *    la TVA des transports de personnes, hébergements et frais postaux, qui
 *    n'est pas récupérable : l'absence de taux n'y prouve donc RIEN. Conclure
 *    « pas de TVA » exonérerait une facture qui en porte. On rend
 *    « indéterminé », et la facture part en saisie manuelle.
 */
export function tvaPresenteEffective(args: {
  constatExtraction?: boolean | null;
  tauxDesLignes: Array<number | null | undefined>;
  typeDepense?: string | null;
  /** Les totaux tels que l'extraction les a lus sur le justificatif. */
  totauxExtraits?: { total_ht?: number | null; total_vat?: number | null; total_ttc?: number | null } | null;
}): boolean | null {
  if (args.tauxDesLignes.some((t) => typeof t === 'number' && t > 0)) return true;
  // Un taux explicitement à ZÉRO est une RÉPONSE, pas un silence : « TVA 0,00 »
  // sur une facture irlandaise dit qu'il n'y a pas de TVA, et le constat
  // d'extraction, lui, répondait « oui, il y a une ligne de TVA » en voyant ce
  // zéro — d'où un « étranger avec TVA étrangère » qui n'existait pas. Un zéro
  // ne se confond pas avec `null`, qui signifie « rien de saisi ».
  if (args.tauxDesLignes.some((t) => t === 0)) return false;
  // LES TOTAUX AVANT LE CONSTAT. Une facture dont le HT égale le TTC, sans
  // montant de TVA, n'en porte pas : c'est de l'arithmétique, là où le constat
  // n'est qu'un jugement porté sur un document. Anthropic, facture en dollars,
  // annonçait « tva_presente = true » avec HT 15,04 = TTC 15,04 et aucune TVA —
  // la facture partait en « étranger avec TVA étrangère », cas hors spec.
  const t = args.totauxExtraits;
  if (t && typeof t.total_ht === 'number' && typeof t.total_ttc === 'number') {
    const sansEcart = Math.abs(t.total_ht - t.total_ttc) < 0.01;
    const sansMontantDeTva = t.total_vat === null || t.total_vat === undefined || t.total_vat === 0;
    if (sansEcart && sansMontantDeTva) return false;
  }
  if (typeof args.constatExtraction === 'boolean') return args.constatExtraction;
  if (args.typeDepense && TVA_ECARTEE_PAR_LES_REGLES.includes(args.typeDepense)) return null;
  return false;
}

export function regimeTva(e: EntreesRegime): RegimeTva {
  // TVA D'UN AUTRE PAYS, payée sur place : elle ne se déduit pas en France, et
  // la dépense est déjà en TTC (les règles d'extraction l'y mettent). Tranché
  // AVANT le pays et avant la présence de TVA : une facture espagnole avec TVA
  // espagnole serait sinon partie en « manuel » comme cas hors spec.
  if (e.tvaEtrangere === true) {
    return { cas: 'E', taxId: TAX_ID_TVA_ETRANGERE, taxBasisType: baseExigibilite(e) };
  }

  if (e.pays !== 'FR' && e.pays !== 'UE' && e.pays !== 'HORS_UE') {
    return {
      cas: 'manuel',
      motif:
        'Le pays du fournisseur n’a pas pu être déterminé : impossible de choisir entre TVA française et exonération.',
    };
  }
  const estFrancais = e.pays === 'FR';

  // TVA NON DÉDUCTIBLE (transport de personnes, hébergement, frais postaux).
  // Tranché AVANT la question de la présence de TVA, et c'est volontaire : que
  // le justificatif la détaille ou non, le traitement est le même — montant en
  // TTC, taxe à 0 %, rien de déduit. C'est aussi ce qui débloque les vieux
  // justificatifs, extraits avant que `tva_presente` n'existe.
  if (estFrancais && tvaNonDeductible(e.typeDepense)) {
    return { cas: 'A', taxBasisType: baseExigibilite(e) };
  }

  if (e.tvaPresente !== true && e.tvaPresente !== false) {
    return {
      cas: 'manuel',
      motif:
        'La présence de TVA sur le justificatif n’a pas pu être déterminée : aucune ligne ne porte de taux, et le type de dépense (transport, hébergement, frais postaux) est précisément un cas où la TVA existe mais n’est pas récupérable.',
    };
  }

  if (estFrancais && e.tvaPresente) {
    return { cas: 'A', taxBasisType: baseExigibilite(e) };
  }

  if (!estFrancais && !e.tvaPresente) {
    return {
      cas: 'B',
      taxId: TAX_ID_AUTOLIQUIDATION_ETRANGER,
      taxBasisType: baseExigibilite(e),
    };
  }

  // Fournisseur français sans TVA : auto-entrepreneur en franchise de base
  // (art. 293 B). Même structure que le cas B, mais une taxe DISTINCTE : ce
  // n'est pas la même situation fiscale (une dispense liée au chiffre
  // d'affaires n'est pas un achat hors de France), et il faudra pouvoir les
  // retrouver séparément.
  //
  // Une ligne réellement exonérée au titre de l'article 261 (banque, assurance)
  // ne passe pas par ici : son code VATEX est EXACT, il est choisi à l'écran
  // ligne par ligne, et il prime sur le régime de la facture.
  if (estFrancais && !e.tvaPresente) {
    return { cas: 'C', taxId: TAX_ID_FRANCHISE_293B, taxBasisType: baseExigibilite(e) };
  }

  // FOURNISSEUR ÉTRANGER IMMATRICULÉ EN FRANCE : Microsoft Irlande, Google,
  // Amazon… facturent sous un numéro de TVA français et appliquent la TVA
  // française, qui se déduit comme n'importe quelle autre. Le siège social ne
  // décide de rien : c'est la TVA PORTÉE par la facture qui commande, principe
  // déjà posé pour la franchise en base.
  //
  // UNE SEULE PREUVE : le numéro de TVA. Il est la preuve JURIDIQUE de
  // l'immatriculation, là où un taux n'est qu'un indice — 20 % se pratique
  // aussi en Autriche, et déduire une TVA autrichienne serait une faute.
  if (estImmatriculeEnFrance(e)) {
    return { cas: 'A', taxBasisType: baseExigibilite(e) };
  }

  // Reste le cas hors spec : une facture étrangère qui porte une TVA dont on ne
  // peut pas dire qu'elle est française. On REFUSE plutôt que de deviner : la
  // traiter en TVA étrangère la passerait en TTC et ferait perdre, en silence,
  // une TVA peut-être déductible — c'est le cas d'un fournisseur immatriculé en
  // France dont le numéro n'a pas été lu.
  if (e.tvaNonRecuperableDecidee === true) {
    return { cas: 'NR', taxId: TAX_ID_TVA_NON_DEDUCTIBLE, taxBasisType: baseExigibilite(e) };
  }
  const numeroLu = (e.numeroTvaIntra ?? '').trim();
  return {
    cas: 'manuel',
    motif:
      `Fournisseur étranger dont la TVA ne peut pas être rattachée à la France : ` +
      `numéro de TVA lu sur la facture = ${numeroLu ? `« ${numeroLu} »` : 'AUCUN'}. ` +
      `Un numéro commençant par FR aurait rendu cette TVA déductible ; ` +
      `s’il figure bien sur le document, relance l’extraction. ` +
      `Si le justificatif porte une TVA à ZÉRO (fournisseur européen facturant hors taxe), ` +
      `relance aussi : une TVA nulle n’est pas une TVA, et la facture partira en autoliquidation.`,
  };
}

/**
 * La case « TVA non récupérable — enregistrer en TTC » a-t-elle lieu d'être ?
 *
 * Vrai EXACTEMENT quand `regimeTva` rendrait le refus « TVA non rattachable à la
 * France » : c'est la seule situation où la décision humaine a un sens. Lue par
 * le serveur (qui refuse une case cochée ailleurs) ET par l'écran (qui ne
 * l'affiche que là) : la règle n'existe qu'à un endroit.
 *
 * Un fournisseur immatriculé en France (numéro FR) ne la voit jamais : sa TVA
 * se déduit, et la cocher la ferait perdre.
 */
export function peutDeclarerTvaNonRecuperable(e: EntreesRegime): boolean {
  // La décision n'ouvre le cas NR qu'au point du refus : si elle y mène, c'est
  // que sans elle la facture était refusée pour cette raison-là, et aucune autre.
  return regimeTva({ ...e, tvaNonRecuperableDecidee: true }).cas === 'NR';
}

/** Le fournisseur facture-t-il sous un numéro de TVA français ? */
function estImmatriculeEnFrance(e: EntreesRegime): boolean {
  const numero = (e.numeroTvaIntra ?? '').replace(/[\s.-]/g, '').toUpperCase();
  return numero.startsWith('FR');
}

/**
 * `accrual` (comptabilité d'exercice) ou `cash` (encaissements) ?
 *
 * Règle métier du §4 : les BIENS et les factures portant la mention « TVA sur
 * les débits » relèvent de l'exercice ; une prestation de services sans cette
 * mention relève des encaissements. C'est le cas majoritaire chez KESTIO, dont
 * les fournisseurs sont surtout des prestataires — d'où ce défaut.
 */
function baseExigibilite(e: EntreesRegime): TaxBasisType {
  if (e.mentionTvaSurLesDebits === true) return 'accrual';
  if ((e.typesProduit ?? []).includes('goods')) return 'accrual';
  return 'cash';
}

/**
 * Passe en TTC les lignes dont le taux est retenu par `aConvertir`, et vide ce
 * taux : la TVA n'est pas récupérable, elle fait partie de la charge.
 *
 * PUR, et partagé par les deux cas qui le demandent — un taux ÉTRANGER resté
 * sur une ligne (cas E) et la TVA française déclarée non récupérable (cas NR) :
 * un même calcul ne doit vivre qu'à un endroit. Arrondi au centime sur le prix
 * unitaire, comme l'a toujours fait le rattrapage des taux étrangers.
 */
export function lignesEnTtc<L extends { unit_price_ht: number; vat_rate?: number | null }>(
  lignes: readonly L[],
  aConvertir: (taux: number) => boolean,
): L[] {
  return lignes.map((l) =>
    typeof l.vat_rate === 'number' && aConvertir(l.vat_rate)
      ? { ...l, unit_price_ht: Math.round(l.unit_price_ht * (1 + l.vat_rate / 100) * 100) / 100, vat_rate: null }
      : l,
  );
}

/**
 * Écart, en euros, entre le total des lignes et le TTC lu sur la facture.
 *
 * Un fournisseur qui calcule sa TVA sur le TOTAL et non ligne par ligne fait
 * apparaître quelques centimes d'écart une fois les lignes passées en TTC. On ne
 * bloque pas pour cela, mais on le DIT : sinon la facture enregistrée ne
 * correspondrait plus au montant payé, sans que personne le sache. Rend `null`
 * quand le TTC lu manque.
 */
export function ecartAvecTtcLu(
  lignes: ReadonlyArray<{ unit_price_ht: number; quantity: number }>,
  ttcLu: number | null | undefined,
): number | null {
  if (typeof ttcLu !== 'number') return null;
  const total = lignes.reduce((s, l) => s + Math.round(l.quantity * l.unit_price_ht * 100) / 100, 0);
  return Math.round((total - ttcLu) * 100) / 100;
}

/**
 * Refus du cas NR quand AUCUNE ligne ne porte de taux, ou null.
 *
 * Seules les lignes taxées passent en TTC. Une facture dont on a vidé les taux
 * (geste naturel de qui voulait « retirer la TVA » avant que la case existe)
 * prouve encore sa TVA par ses totaux, donc la case est proposée — mais ses
 * lignes partiraient au montant HORS TAXE, sous une taxe qui dit « TTC ». La
 * charge serait sous-évaluée de la TVA payée, en silence. PUR : l'écran et le
 * serveur disent la même chose.
 */
export function motifLignesSansTaux(tauxDesLignes: Array<number | null | undefined>): string | null {
  if (tauxDesLignes.some((t) => typeof t === 'number' && t > 0)) return null;
  return (
    'Aucune ligne ne porte de taux de TVA : remets sur chaque ligne le taux imprimé sur la facture ' +
    '(20 % en général). Sans lui, les lignes partiraient au montant HORS TAXE alors que le TTC a été payé.'
  );
}
